import React from 'react';

import i18n from '@yandex-int/i18n';
import { MonitoringPageChartStub } from 'client/blocks/MonitoringPageChartStub/MonitoringPageChartStub';
import { textToKey } from 'client/lib/i18n-utils';
import { AreaChartType, BarChartType, ChartType, DashboardRowMeta } from 'client/model/solomon';
import { LoadingDataReducerStateType } from 'client/reducers/loadingData';
import { barsToData, timeseriesToData } from 'client/utils/monitoring';
import * as keyset from 'i18n/Monitoring';

import styles from './AliceAppMonitoring.module.scss';
import {
    AliceAppMonitoringPageAreaChart,
    AliceAppMonitoringPageSmartHomeTiming,
} from './AliceAppMonitoringPageAreaChart';
import { AliceAppMonitoringPageBarChartErrorSwitch } from './AliceAppMonitoringPageBarChartErrorSwitch';

const t = i18n(keyset);

type I18NLiteral = keyof (typeof keyset)[keyof typeof keyset];

interface Props {
    charts: DashboardRowMeta['charts'];
    from: number;
    to: number;
    dataLoadingState: LoadingDataReducerStateType;
    onIntervalChange: (startTs: number, endTs: number) => void;
}

export const AliceAppMonitoringSection: React.FC<Props> = ({
    charts,
    from,
    to,
    dataLoadingState,
    onIntervalChange,
}) => {
    return (
        <div>
            {charts.map((chart) => {
                const { type, yAxisLabel, yAxisThreshold, yAxisThresholdLabel, id } = chart;

                switch (type) {
                    case ChartType.Area: {
                        const { data: timeseries } = chart as AreaChartType;
                        const areaChartData = timeseriesToData(timeseries);

                        const AreaChartComp =
                            chart.id === 'SMART_HOME_REQUEST_TIMINGS'
                                ? AliceAppMonitoringPageSmartHomeTiming
                                : AliceAppMonitoringPageAreaChart;
                        return (
                            <div key={id} className={styles.monitoringSection}>
                                {areaChartData ? (
                                    <AreaChartComp
                                        onIntervalChange={onIntervalChange}
                                        data={areaChartData.data}
                                        labels={areaChartData.labels.filter((label) => label !== 'total')}
                                        dataLoadingState={dataLoadingState}
                                        from={from}
                                        to={to}
                                        yAxisLabel={
                                            yAxisLabel !== undefined
                                                ? t(textToKey(yAxisLabel) as I18NLiteral)
                                                : undefined
                                        }
                                        yAxisThreshold={yAxisThreshold}
                                        yAxisThresholdLabel={yAxisThresholdLabel}
                                        disableStacking={['SMART_HOME_REQUEST_TIMINGS', 'REQUEST_TIMINGS'].includes(
                                            chart.id,
                                        )}
                                    />
                                ) : (
                                    <MonitoringPageChartStub
                                        fromTs={from}
                                        toTs={to}
                                        dataLoadingState={dataLoadingState}
                                    />
                                )}
                            </div>
                        );
                    }
                    case ChartType.Bar: {
                        const { data: bars } = chart as BarChartType;
                        const barChartData = barsToData(bars);

                        return (
                            <div key={id} className={styles.monitoringSection}>
                                {barChartData ? (
                                    <AliceAppMonitoringPageBarChartErrorSwitch
                                        dataLoadingState={dataLoadingState}
                                        data={barChartData.data}
                                        labels={barChartData.labels}
                                        yAxisLabel={
                                            yAxisLabel !== undefined
                                                ? t(textToKey(yAxisLabel) as I18NLiteral)
                                                : undefined
                                        }
                                        displaySwitch={chart.displayErrorTypeSwitch}
                                    />
                                ) : (
                                    <MonitoringPageChartStub
                                        hideXAxis
                                        fromTs={from}
                                        toTs={to}
                                        dataLoadingState={dataLoadingState}
                                    />
                                )}
                            </div>
                        );
                    }
                }
            })}
        </div>
    );
};
